import {FC, useCallback, useMemo, useState, RefObject} from "react";
import {Box, BoxProps, Select, MenuItem} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {Masonry} from "@mui/lab";
import {NoteInput} from "./NoteInput";
import {NoteItem} from "./NoteItem";
import {useSearchNotes, useCreateNote} from "@/app2/shared/data";
import type {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";

enum SORT_BY {
	OLDEST = "OLDEST",
	LATEST = "LATEST"
}

const useStyles = makeStyles()((theme) => ({
	notesSection: {
		marginTop: theme.spacing(6)
	},
	filtersContainer: {
		display: "flex",
		height: "100%",
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column",
			flexGrow: 1
		}
	},
	selectFilter: {
		backgroundColor: "transparent"
	},
	inputContainer: {
		display: "flex",
		gap: theme.spacing(2),
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column"
		}
	},
	masonryRoot: {
		width: "calc(100% + 1.5rem)",
		marginTop: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			"&>*": { width: `calc(100.00% - ${theme.spacing(3)})` }
		}
	}
}));

export interface NotesTranslations {
	mostRecent?: string;
	oldestFirst?: string;
	noteInputPlaceholder?: string;
}

interface NotesSectionProps extends BoxProps {
	lessonEnrollmentId?: number | null;
	videoRef?: RefObject<VideoPlayerRef | null>;
	disableTimeStamp?: boolean;
	onGoToModuleAction?: (courseSlug: string, moduleSlug: string, moduleType: string, timestamp?: number) => void;
	translations?: NotesTranslations;
}

export const NotesSection: FC<NotesSectionProps> = ({
	lessonEnrollmentId,
	videoRef,
	disableTimeStamp,
	onGoToModuleAction = () => {},
	translations,
	...baseProps
}) => {
	const [sortBy, setSortBy] = useState<SORT_BY>(SORT_BY.LATEST);
	const {classes, cx} = useStyles();

	const {data, refetch} = useSearchNotes({
		lessonEnrollmentId: lessonEnrollmentId ?? undefined
	});

	const {mutate: createNote, isPending: isCreating} = useCreateNote();

	const getStringTranslation = useCallback(
		(key: keyof NotesTranslations, fallback: string): string => {
			const t = translations?.[key];
			return typeof t === "string" ? t : fallback;
		},
		[translations]
	);

	const handlePostNote = useCallback(
		(note: { content: string; videoTimestamp?: number }) => {
			if (!lessonEnrollmentId) return;
			createNote(
				{
					content: note.content,
					videoTimestamp: note.videoTimestamp ?? null,
					lessonEnrollmentId
				},
				{
					onSuccess: () => refetch()
				}
			);
		},
		[lessonEnrollmentId, createNote, refetch]
	);

	const sortedNotes = useMemo(() => {
		const notes = data?.notes ?? [];
		return [...notes].sort((a, b) => {
			const dateA = new Date(a.createdAt).getTime();
			const dateB = new Date(b.createdAt).getTime();
			return sortBy === SORT_BY.OLDEST ? dateA - dateB : dateB - dateA;
		});
	}, [data?.notes, sortBy]);

	return (
		<Box className={classes.notesSection} {...baseProps} key={lessonEnrollmentId ?? "notes"}>
			<Box className={classes.inputContainer}>
				<NoteInput
					disableTimeStamp={disableTimeStamp}
					postNote={handlePostNote}
					videoRef={videoRef as RefObject<{ bitmovin?: { player?: { getCurrentTime: () => number } } } | null>}
					placeholder={getStringTranslation("noteInputPlaceholder", "Type your private note")}
				/>
				<Box className={classes.filtersContainer}>
					<Select
						value={sortBy}
						className={cx(classes.selectFilter)}
						variant="outlined"
						onChange={(e) => setSortBy(e.target.value as SORT_BY)}
					>
						<MenuItem value={SORT_BY.LATEST}>{getStringTranslation("mostRecent", "Most recent")}</MenuItem>
						<MenuItem value={SORT_BY.OLDEST}>{getStringTranslation("oldestFirst", "Oldest first")}</MenuItem>
					</Select>
				</Box>
			</Box>
			<Masonry columns={{xs: 1, sm: 1, md: 2}} defaultColumns={1} spacing={3} className={classes.masonryRoot}>
				{sortedNotes.map((note) => (
					<NoteItem
						key={note.id}
						note={note}
						onGoToModule={onGoToModuleAction}
						onRefresh={() => refetch()}
					/>
				))}
			</Masonry>
		</Box>
	);
};
